import { createMachine, transition } from "xstate";
import { BookingStatus } from "./booking";

// The booking lifecycle as an explicit finite state machine. State ids are the
// `BookingStatus` values and events are the staff actions, so the machine and
// the backend API contract share one vocabulary. This machine is the single
// source of truth for which status transitions are legal; the UI only ever
// offers the moves it accepts.
export type BookingEventType =
  | "CONFIRM"
  | "CANCEL"
  | "CHECK_IN"
  | "NO_SHOW"
  | "CORRECT_TO_CHECKED_IN";

export type BookingEvent = { type: BookingEventType };

export const bookingMachine = createMachine({
  id: "booking",
  initial: BookingStatus.PENDING,
  types: {} as { events: BookingEvent },
  states: {
    [BookingStatus.PENDING]: {
      on: {
        CONFIRM: BookingStatus.CONFIRMED,
        CANCEL: BookingStatus.CANCELED,
      },
    },
    [BookingStatus.CONFIRMED]: {
      on: {
        CHECK_IN: BookingStatus.CHECKED_IN,
        NO_SHOW: BookingStatus.NO_SHOW,
        CANCEL: BookingStatus.CANCELED,
      },
    },
    [BookingStatus.CHECKED_IN]: {
      on: { NO_SHOW: BookingStatus.NO_SHOW },
    },
    [BookingStatus.NO_SHOW]: {
      on: { CORRECT_TO_CHECKED_IN: BookingStatus.CHECKED_IN },
    },
    [BookingStatus.CANCELED]: { type: "final" },
  },
});

export interface BookingTransition {
  event: BookingEventType;
  target: BookingStatus;
}

// Every staff action the machine knows about, in the order controls should be
// presented. `target` is the resulting status; `event` is what drives it and is
// what disambiguates actions that share a target (CHECK_IN vs
// CORRECT_TO_CHECKED_IN both land on CHECKED_IN).
const BOOKING_TRANSITIONS: BookingTransition[] = [
  { event: "CONFIRM", target: BookingStatus.CONFIRMED },
  { event: "CHECK_IN", target: BookingStatus.CHECKED_IN },
  { event: "NO_SHOW", target: BookingStatus.NO_SHOW },
  { event: "CORRECT_TO_CHECKED_IN", target: BookingStatus.CHECKED_IN },
  { event: "CANCEL", target: BookingStatus.CANCELED },
];

// Derive the legal transitions from the machine itself rather than a
// hand-maintained map: for each known event, ask the machine to transition from
// `status` and keep the events that actually change state. Adding or removing a
// transition is then a one-line edit to the machine config above.
export const getAllowedBookingTransitions = (
  status: BookingStatus,
): BookingTransition[] => {
  const snapshot = bookingMachine.resolveState({ value: status });
  return BOOKING_TRANSITIONS.filter(({ event }) => {
    const [next] = transition(bookingMachine, snapshot, { type: event });
    return next.value !== status;
  });
};
